import { useState, useEffect, useCallback, useRef } from "react";
import {
  Box,
  Button,
  TextField,
  Typography,
  Alert,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
  IconButton,
  Tooltip,
  CircularProgress,
  Popover,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Chip,
  Switch,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import dayjs from "dayjs";
import AppDialog from "../General/Dialog/AppDialog";
import CalculateRoundedIcon from "@mui/icons-material/CalculateRounded";
import SaveRoundedIcon from "@mui/icons-material/SaveRounded";
import EditRoundedIcon from "@mui/icons-material/EditRounded";
import CheckRoundedIcon from "@mui/icons-material/CheckRounded";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
import DeleteOutlineRoundedIcon from "@mui/icons-material/DeleteOutlineRounded";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import {
  calculateLiquidation,
  storeLiquidation,
  generateLiquidationPdf,
  fetchTributarioBalance,
  getLiquidationItemTypes,
} from "../../services/LiquidationService";
import { useTheme } from "@mui/material/styles";
import { formatCurrency } from "../../config/Helper";
import CurrencyInput from "../General/CurrencyInput";

// Mapeo de códigos internos del sistema → códigos del API tributario
const REP_ALIAS = {
  COM: "CI",
};

const REP_OPTIONS = [
  { cod: " ", nombre: " " },
  { cod: "PCP", nombre: "Planes de Pago" },
  { cod: "FYM", nombre: "Ferias y Mercados" },
  { cod: "PYP", nombre: "Publicidad y Propaganda" },
  { cod: "CPM", nombre: "Contrib. por Mejoras" },
  { cod: "CI", nombre: "CI (Comercio e Industria)" },
  { cod: "TA", nombre: "TA (Tasa Administrativa)" },
  { cod: "CLO", nombre: "CLO (cloacas)" },
  { cod: "INM", nombre: "INM (Inmueble)" },
  { cod: "AUT", nombre: "AUT (Automotor)" },
  { cod: "CEM", nombre: "CEM (Cementerio)" },
  { cod: "FIS", nombre: "FIS (fiscalización)" },
  { cod: "DV", nombre: "DV (Derechos Varios)" },
  { cod: "TF", nombre: "TF (Multas-Juzg Faltas)" },
];

const CALC_LABELS = {
  jus_multiplier: "JUS",
  percentage_of_amount: "% Monto",
  interest_on_tax: "Interés",
  fixed_catalog: "Catálogo",
  manual: "Manual",
};

const CATEGORY_LABELS = {
  capital: "Capital",
  honorarios: "Honorarios",
  gastos: "Gastos",
};

// ── EditableAmountCell ────────────────────────────────────────────────────────

function EditableAmountCell({ value, onChange }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value ?? null);

  const commit = () => {
    if (draft != null) onChange(draft);
    setEditing(false);
  };

  const cancel = () => {
    setDraft(value ?? null);
    setEditing(false);
  };

  if (editing) {
    return (
      <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
        <CurrencyInput
          size="small"
          value={draft}
          onValueChange={({ floatValue }) => setDraft(floatValue ?? null)}
          onKeyDown={(e) => {
            if (e.key === "Enter") commit();
            if (e.key === "Escape") cancel();
          }}
          autoFocus
          sx={{ width: 140 }}
        />
        <IconButton size="small" onClick={commit} color="success">
          <CheckRoundedIcon fontSize="small" />
        </IconButton>
        <IconButton size="small" onClick={cancel}>
          <CloseRoundedIcon fontSize="small" />
        </IconButton>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 0.5,
        justifyContent: "flex-end",
      }}
    >
      <Typography sx={{ fontWeight: 700, fontSize: 13 }}>
        {formatCurrency(value)}
      </Typography>
      <Tooltip title="Editar monto">
        <IconButton
          size="small"
          onClick={() => {
            setDraft(value ?? null);
            setEditing(true);
          }}
        >
          <EditRoundedIcon sx={{ fontSize: 14, opacity: 0.4 }} />
        </IconButton>
      </Tooltip>
    </Box>
  );
}

// ── CategoryBadge ─────────────────────────────────────────────────────────────

function CategoryBadge({ category, isDark }) {
  return (
    <Box
      sx={{
        display: "inline-flex",
        alignItems: "center",
        px: 1,
        py: 0.3,
        borderRadius: 1,
        background: isDark ? "rgba(255,255,255,0.07)" : "rgba(0,0,0,0.055)",
        border: isDark
          ? "1px solid rgba(255,255,255,0.1)"
          : "1px solid rgba(0,0,0,0.1)",
        backdropFilter: "blur(8px)",
      }}
    >
      <Typography
        sx={{
          fontSize: 10,
          fontWeight: 700,
          letterSpacing: 0.6,
          textTransform: "uppercase",
          lineHeight: 1,
          opacity: isDark ? 0.85 : 0.7,
        }}
      >
        {CATEGORY_LABELS[category] ?? category}
      </Typography>
    </Box>
  );
}

// ── Main dialog ───────────────────────────────────────────────────────────────

export default function LiquidationFormDialog({
  open,
  onClose,
  legalFile,
  onSaved,
}) {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";

  const glass = {
    background: isDark ? "rgba(255,255,255,0.05)" : "rgba(255,255,255,0.6)",
    backdropFilter: "blur(12px)",
    border: isDark
      ? "1px solid rgba(255,255,255,0.09)"
      : "1px solid rgba(0,0,0,0.07)",
    borderRadius: 2,
  };

  const glassPanel = {
    background: isDark ? "rgba(255,255,255,0.06)" : "rgba(255,255,255,0.75)",
    backdropFilter: "blur(16px)",
    border: isDark
      ? "1px solid rgba(255,255,255,0.1)"
      : "1px solid rgba(0,0,0,0.08)",
    borderRadius: 2.5,
    boxShadow: isDark
      ? "0 4px 32px rgba(0,0,0,0.35)"
      : "0 4px 24px rgba(0,0,0,0.07)",
  };

  const [lawsuitAmount, setLawsuitAmount] = useState(null);
  const [lawsuitDate, setLawsuitDate] = useState(null);
  const [tributarioDate, setTributarioDate] = useState(dayjs());
  const [tributarioBalance, setTributarioBalance] = useState(null);
  const [tributarioResult, setTributarioResult] = useState(null);
  const [annualRate, setAnnualRate] = useState("24");
  const [notes, setNotes] = useState("");
  // La Municipalidad pide a veces los rubros de Honorarios en JUS en vez de
  // pesos — desactivado por default, la operadora lo prende a mano cuando
  // corresponde para esta liquidación puntual.
  const [honorariosInJus, setHonorariosInJus] = useState(false);

  const [calcResult, setCalcResult] = useState(null);
  const [items, setItems] = useState([]);
  const [calcLoading, setCalcLoading] = useState(false);
  const [calcError, setCalcError] = useState("");

  const [saveLoading, setSaveLoading] = useState(false);
  const [saveError, setSaveError] = useState("");

  // Available item types for adding rows
  const [availableItemTypes, setAvailableItemTypes] = useState([]);
  const [addItemAnchor, setAddItemAnchor] = useState(null);
  const [addItemSearch, setAddItemSearch] = useState("");

  const balanceFromSearchRef = useRef(false);

  // Tributario search popover
  const [searchAnchor, setSearchAnchor] = useState(null);
  const [searchRep, setSearchRep] = useState("INM");
  const [searchNro, setSearchNro] = useState("");
  const [searchAnio, setSearchAnio] = useState(String(dayjs().year()));
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchError, setSearchError] = useState("");
  const [searchFound, setSearchFound] = useState(null);

  useEffect(() => {
    if (!open || !legalFile) return;

    let cancelled = false;

    setLawsuitAmount(legalFile.lawsuits?.amount ?? null);
    setLawsuitDate(legalFile.start_date ? dayjs(legalFile.start_date) : null);
    setTributarioDate(dayjs());
    setTributarioBalance(null);
    setTributarioResult(null);
    setAnnualRate("24");
    setNotes("");
    setHonorariosInJus(false);
    setCalcResult(null);
    setItems([]);
    setCalcError("");
    setSaveError("");
    setSearchFound(null);
    setSearchError("");

    // Parse lawsuit code → pre-fill search fields + auto-fetch balance
    const lawsuitCode = legalFile.lawsuits?.code;
    let parsedRep = "INM", parsedNro = "", parsedAnio = "";

    if (lawsuitCode) {
      const parts = lawsuitCode.split("/");
      const rawRep = parts[0]?.trim().toUpperCase() || "INM";
      parsedRep = REP_ALIAS[rawRep] ?? rawRep;
      parsedNro = parts[1]?.trim() || "";
      parsedAnio = parts[2]?.trim().slice(0, 4) || String(dayjs().year());
    } else {
      parsedAnio = legalFile.start_date
        ? String(dayjs(legalFile.start_date).year())
        : String(dayjs().year());
    }

    setSearchRep(parsedRep);
    setSearchNro(parsedNro);
    setSearchAnio(parsedAnio);

    getLiquidationItemTypes({ per_page: 200 })
      .then((r) => { if (!cancelled) setAvailableItemTypes(r.data?.data ?? r.data ?? []); })
      .catch(() => {});

    return () => { cancelled = true; };
  }, [open, legalFile]);

  const handleTributarioSearch = async () => {
    setSearchLoading(true);
    setSearchError("");
    setSearchFound(null);
    try {
      const rawRep = searchRep.trim().toUpperCase();
      const res = await fetchTributarioBalance({
        rep: REP_ALIAS[rawRep] ?? rawRep,
        nro: parseInt(searchNro, 10),
        anio: parseInt(searchAnio, 10),
      });
      setSearchFound(res.data);
      balanceFromSearchRef.current = true;
      setTributarioBalance(res.data.balance);
      setTributarioResult(res.data);
    } catch (e) {
      setSearchError(
        e?.response?.status === 404
          ? "No se encontró el juicio en tributariomuni."
          : (e?.response?.data?.message ??
              "Error al consultar tributariomuni."),
      );
    } finally {
      setSearchLoading(false);
    }
  };

  const handleCalculate = useCallback(async () => {
    setCalcLoading(true);
    setCalcError("");
    try {
      const payload = {
        annual_rate: annualRate ? parseFloat(annualRate) : undefined,
        lawsuit_amount: lawsuitAmount ?? undefined,
        lawsuit_date: lawsuitDate
          ? lawsuitDate.format("YYYY-MM-DD")
          : undefined,
        tributario_date: tributarioDate
          ? tributarioDate.format("YYYY-MM-DD")
          : undefined,
        tributario_balance: tributarioBalance ?? undefined,
      };
      const res = await calculateLiquidation(legalFile.id, payload);
      setCalcResult(res.data);
      setItems(res.data.items ?? []);
    } catch (e) {
      setCalcError(
        e?.response?.data?.message ?? "Error al calcular la liquidación.",
      );
    } finally {
      setCalcLoading(false);
    }
  }, [
    legalFile,
    annualRate,
    lawsuitAmount,
    lawsuitDate,
    tributarioDate,
    tributarioBalance,
  ]);

  const handleItemAmountChange = (idx, newAmount) => {
    setItems((prev) =>
      prev.map((it, i) => (i === idx ? { ...it, amount: newAmount } : it)),
    );
  };

  const handleDeleteItem = (idx) => {
    setItems((prev) => prev.filter((_, i) => i !== idx));
  };

  const handleAddItemType = async (itemType) => {
    setAddItemAnchor(null);
    setAddItemSearch("");

    const payload = {
      annual_rate: annualRate ? parseFloat(annualRate) : undefined,
      lawsuit_amount: lawsuitAmount ?? undefined,
      lawsuit_date: lawsuitDate ? lawsuitDate.format("YYYY-MM-DD") : undefined,
      tributario_date: tributarioDate
        ? tributarioDate.format("YYYY-MM-DD")
        : undefined,
      tributario_balance: tributarioBalance ?? undefined,
    };

    try {
      const res = await calculateLiquidation(legalFile.id, payload);
      const calcItems = res.data?.items ?? [];
      const calcById = Object.fromEntries(
        calcItems.map((ci) => [ci.liquidation_item_type_id, ci]),
      );
      setCalcResult(res.data);

      setItems((prev) => {
        // Update all non-custom existing items with fresh calculated values
        const updated = prev.map((it) => {
          if (it.is_custom) return it;
          const fresh = calcById[it.liquidation_item_type_id];
          if (!fresh) return it;
          return {
            ...it,
            amount: fresh.amount,
            iva_amount: fresh.iva_amount ?? 0,
            metadata: fresh.metadata ?? null,
          };
        });

        // Add the new item if not already in the list
        if (
          !updated.some((it) => it.liquidation_item_type_id === itemType.id)
        ) {
          const fresh = calcById[itemType.id];
          updated.push({
            liquidation_item_type_id: itemType.id,
            category: itemType.category,
            calculation_type: itemType.calculation_type,
            name: itemType.name,
            amount: fresh?.amount ?? null,
            iva_amount: fresh?.iva_amount ?? 0,
            is_custom: !fresh,
            sort_order: fresh?.sort_order ?? itemType.sort_order,
            metadata: fresh?.metadata ?? null,
          });
        }

        return updated;
      });
    } catch {
      // Fallback: add with null amount so the user can edit it manually
      setItems((prev) => {
        if (prev.some((it) => it.liquidation_item_type_id === itemType.id))
          return prev;
        return [
          ...prev,
          {
            liquidation_item_type_id: itemType.id,
            category: itemType.category,
            calculation_type: itemType.calculation_type,
            name: itemType.name,
            amount: null,
            iva_amount: 0,
            is_custom: true,
            metadata: null,
          },
        ];
      });
    }
  };

  const handleSave = async () => {
    if (items.length === 0) return;
    setSaveLoading(true);
    setSaveError("");
    try {
      const payload = {
        lawsuit_amount: calcResult?.lawsuit_amount ?? lawsuitAmount ?? null,
        lawsuit_date:
          calcResult?.lawsuit_date ??
          (lawsuitDate ? lawsuitDate.format("YYYY-MM-DD") : null),
        tributario_date:
          calcResult?.tributario_date ??
          (tributarioDate ? tributarioDate.format("YYYY-MM-DD") : null),
        tributario_balance:
          calcResult?.tributario_balance ?? tributarioBalance ?? null,
        tributario_jud_id: tributarioResult?.jud_id ?? null,
        jus_value: calcResult?.jus_value ?? null,
        annual_rate:
          calcResult?.annual_rate ??
          (annualRate ? parseFloat(annualRate) : null),
        honorarios_in_jus: honorariosInJus,
        notes: notes || null,
        items: items.map((it, idx) => ({
          liquidation_item_type_id: it.liquidation_item_type_id,
          amount: it.amount,
          iva_amount: it.iva_amount ?? 0,
          is_custom: it.is_custom ?? false,
          sort_order: it.sort_order ?? idx,
          metadata: it.metadata ?? null,
        })),
      };
      const res = await storeLiquidation(legalFile.id, payload);
      try {
        await generateLiquidationPdf(res.data.id);
      } catch {
        // PDF failure is non-blocking
      }
      onSaved?.();
      onClose();
    } catch (e) {
      setSaveError(
        e?.response?.data?.message ?? "Error al guardar la liquidación.",
      );
    } finally {
      setSaveLoading(false);
    }
  };

  const totalCapital = items
    .filter((i) => i.category === "capital")
    .reduce((s, i) => s + Number(i.amount ?? 0), 0);
  const totalHonorarios = items
    .filter((i) => i.category === "honorarios")
    .reduce((s, i) => s + Number(i.amount ?? 0), 0);
  const totalIva = items.reduce((s, i) => s + Number(i.iva_amount ?? 0), 0);
  const totalGastos = items
    .filter((i) => i.category === "gastos")
    .reduce((s, i) => s + Number(i.amount ?? 0), 0);
  const grandTotal = totalCapital + totalHonorarios + totalIva + totalGastos;

  // Monto en pesos ÷ valor de JUS actual. Solo presentación: el total general
  // (grandTotal, arriba) siempre suma honorarios en pesos.
  const formatHonorarios = (amount) => {
    const jusValue = calcResult?.jus_value;
    if (honorariosInJus && Number(jusValue) > 0) {
      return `${(Number(amount) / Number(jusValue)).toLocaleString("es-AR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} JUS`;
    }
    return formatCurrency(amount);
  };

  const summaryMeta = calcResult
    ? [
        calcResult.jus_value != null && {
          label: "JUS",
          value: formatCurrency(calcResult.jus_value),
        },
        calcResult.annual_rate != null && {
          label: "Tasa anual",
          value: `${calcResult.annual_rate}%`,
        },
        calcResult.lawsuit_date && {
          label: "Fecha título",
          value: calcResult.lawsuit_date,
        },
      ].filter(Boolean)
    : [];

  return (
    <AppDialog
      open={open}
      onClose={onClose}
      title="Nueva Liquidación"
      subtitle={
        legalFile
          ? [
              `Expediente - ${legalFile.code}`,
              legalFile.lawsuits?.code
                ? `Codigo Título ${legalFile.lawsuits.code}`
                : null,
              legalFile.lawsuits?.juc_id
                ? `JUID ${legalFile.lawsuits.juc_id}`
                : null,
            ]
              .filter(Boolean)
              .join(" - ")
          : ""
      }
      maxWidth="lg"
      fullWidth
      fullScreenMobile
      stickyHeader
      actions={
        <>
          <Button
            onClick={onClose}
            disabled={saveLoading}
            sx={{ borderRadius: 2 }}
          >
            Cancelar
          </Button>
          <Button
            variant="contained"
            startIcon={
              saveLoading ? (
                <CircularProgress size={16} color="inherit" />
              ) : (
                <SaveRoundedIcon />
              )
            }
            onClick={handleSave}
            disabled={saveLoading || items.length === 0}
            sx={{ borderRadius: 2, fontWeight: 900 }}
          >
            {saveLoading
              ? "Guardando y generando PDF..."
              : "Guardar Liquidación"}
          </Button>
        </>
      }
    >
      <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5, p: 0.5 }}>
        {/* ── Partes del expediente ── */}
        {legalFile?.parties?.length > 0 && (
          <Box
            sx={{
              ...glass,
              px: 2,
              py: 1.5,
              display: "flex",
              flexWrap: "wrap",
              gap: 1,
              alignItems: "flex-start",
            }}
          >
            <Typography
              sx={{
                fontSize: 9,
                fontWeight: 700,
                textTransform: "uppercase",
                letterSpacing: 0.7,
                opacity: 0.4,
                alignSelf: "center",
                mr: 0.5,
                flexShrink: 0,
              }}
            >
              Partes
            </Typography>
            {legalFile.parties.map((p, i) => {
              const fullName =
                `${p.name || ""} ${p.lastname || ""}`.trim() || "Sin nombre";
              const isTributarioMatch =
                tributarioResult?.nombre &&
                fullName
                  .toLowerCase()
                  .includes(
                    tributarioResult.nombre.split(" ")[0]?.toLowerCase(),
                  );
              return (
                <Box
                  key={p.id ?? i}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 0.75,
                    px: 1.2,
                    py: 0.6,
                    borderRadius: 1.5,
                    background: isDark
                      ? "rgba(255,255,255,0.05)"
                      : "rgba(255,255,255,0.8)",
                    border: isTributarioMatch
                      ? "1px solid rgba(25,118,210,0.35)"
                      : isDark
                        ? "1px solid rgba(255,255,255,0.09)"
                        : "1px solid rgba(0,0,0,0.09)",
                    backdropFilter: "blur(8px)",
                  }}
                >
                  <Box
                    sx={{
                      display: "inline-flex",
                      alignItems: "center",
                      px: 0.75,
                      py: 0.15,
                      borderRadius: 0.75,
                      background: isDark
                        ? "rgba(255,255,255,0.07)"
                        : "rgba(0,0,0,0.055)",
                      border: isDark
                        ? "1px solid rgba(255,255,255,0.1)"
                        : "1px solid rgba(0,0,0,0.1)",
                      flexShrink: 0,
                    }}
                  >
                    <Typography
                      sx={{
                        fontSize: 9,
                        fontWeight: 700,
                        textTransform: "uppercase",
                        letterSpacing: 0.5,
                        lineHeight: 1,
                        opacity: 0.65,
                      }}
                    >
                      {p.role || "—"}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography
                      sx={{ fontSize: 12, fontWeight: 700, lineHeight: 1.2 }}
                    >
                      {fullName}
                    </Typography>
                    {p.document && (
                      <Typography
                        sx={{ fontSize: 10, opacity: 0.5, lineHeight: 1 }}
                      >
                        {p.document}
                      </Typography>
                    )}
                  </Box>
                  {isTributarioMatch && (
                    <Tooltip
                      title={`Coincide con tributario: ${tributarioResult.nombre}`}
                    >
                      <Box
                        sx={{
                          width: 7,
                          height: 7,
                          borderRadius: "50%",
                          bgcolor: "primary.main",
                          flexShrink: 0,
                        }}
                      />
                    </Tooltip>
                  )}
                </Box>
              );
            })}
          </Box>
        )}

        {/* ── Inputs panel ── */}
        <Box sx={{ ...glassPanel, p: 2.5 }}>
          <Typography
            sx={{
              fontSize: 10,
              fontWeight: 700,
              textTransform: "uppercase",
              letterSpacing: 0.7,
              opacity: 0.45,
              mb: 2,
            }}
          >
            Parámetros de cálculo
          </Typography>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: {
                xs: "1fr",
                sm: "1fr 1fr",
                md: "1fr 1fr 1fr",
              },
              gap: 2,
            }}
          >
            <CurrencyInput
              label="Monto título"
              value={lawsuitAmount}
              onValueChange={({ floatValue }) =>
                setLawsuitAmount(floatValue ?? null)
              }
              size="small"
              fullWidth
            />
            <DatePicker
              label="Fecha título"
              value={lawsuitDate}
              onChange={setLawsuitDate}
              slotProps={{ textField: { size: "small", fullWidth: true } }}
              format="DD/MM/YYYY"
            />
            <DatePicker
              label="Fecha Planilla Liquidación"
              value={tributarioDate}
              onChange={setTributarioDate}
              slotProps={{ textField: { size: "small", fullWidth: true } }}
              format="DD/MM/YYYY"
            />
            {/* Saldo tributario + botón búsqueda */}
            <Box>
              <Box
                sx={{ display: "flex", gap: 0.75, alignItems: "flex-start" }}
              >
                <CurrencyInput
                  label="Saldo tributario municipal"
                  value={tributarioBalance}
                  onValueChange={({ floatValue }) => {
                    if (balanceFromSearchRef.current) {
                      balanceFromSearchRef.current = false;
                      return;
                    }
                    setTributarioBalance(floatValue ?? null);
                    if (tributarioResult) setTributarioResult(null);
                  }}
                  size="small"
                  sx={{ flex: 1 }}
                />
                <Tooltip title="Buscar en Tributario Municipal">
                  <IconButton
                    size="small"
                    onClick={(e) => {
                      setSearchAnchor(e.currentTarget);
                      setSearchFound(null);
                      setSearchError("");
                    }}
                    sx={{
                      mt: "4px",
                      border: "1px solid",
                      borderColor: "divider",
                      borderRadius: 1,
                      height: 32,
                      width: 32,
                    }}
                  >
                    <SearchRoundedIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Box>
              {tributarioResult && (
                <Box
                  sx={{ mt: 0.75, display: "flex", gap: 0.5, flexWrap: "wrap" }}
                >
                  <Chip
                    label={`#${tributarioResult.jud_id}`}
                    size="small"
                    color="primary"
                    variant="outlined"
                    sx={{ fontWeight: 700, fontSize: 10 }}
                  />
                  {tributarioResult.expe && (
                    <Chip
                      label={tributarioResult.expe}
                      size="small"
                      variant="outlined"
                      sx={{ fontSize: 10 }}
                    />
                  )}
                  {tributarioResult.nombre && (
                    <Chip
                      label={tributarioResult.nombre}
                      size="small"
                      variant="outlined"
                      sx={{ fontSize: 10 }}
                    />
                  )}
                  {tributarioResult.est_nom && (
                    <Chip
                      label={tributarioResult.est_nom}
                      size="small"
                      color={
                        tributarioResult.est_nom === "Pagado"
                          ? "success"
                          : "default"
                      }
                      sx={{ fontSize: 10 }}
                    />
                  )}
                </Box>
              )}
            </Box>
            <TextField
              label="Tasa de interés anual (%)"
              value={annualRate}
              onChange={(e) => setAnnualRate(e.target.value)}
              size="small"
              type="number"
              inputProps={{ min: 0, max: 100, step: 0.1 }}
            />
            <Box sx={{ display: "flex", alignItems: "flex-end" }}>
              <Button
                fullWidth
                variant="contained"
                startIcon={
                  calcLoading ? (
                    <CircularProgress size={16} color="inherit" />
                  ) : (
                    <CalculateRoundedIcon />
                  )
                }
                onClick={handleCalculate}
                disabled={calcLoading}
                sx={{ borderRadius: 2, fontWeight: 900, height: 40 }}
              >
                Calcular
              </Button>
            </Box>
          </Box>
        </Box>

        {calcError && <Alert severity="error">{calcError}</Alert>}

        {/* ── Summary meta tags ── */}
        {summaryMeta.length > 0 && (
          <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", alignItems: "center" }}>
            <Box
              sx={{
                ...glass,
                pl: 1.5,
                pr: 1,
                py: 0.3,
                display: "flex",
                alignItems: "center",
                gap: 0.5,
              }}
            >
              <Typography
                sx={{
                  fontSize: 10,
                  fontWeight: 700,
                  textTransform: "uppercase",
                  letterSpacing: 0.6,
                  opacity: 0.55,
                }}
              >
                Honorarios en JUS
              </Typography>
              <Switch
                size="small"
                checked={honorariosInJus}
                onChange={(e) => setHonorariosInJus(e.target.checked)}
              />
            </Box>
            {summaryMeta.map(({ label, value }) => (
              <Box
                key={label}
                sx={{
                  ...glass,
                  px: 1.5,
                  py: 0.6,
                  display: "flex",
                  alignItems: "center",
                  gap: 0.75,
                }}
              >
                <Typography
                  sx={{
                    fontSize: 10,
                    fontWeight: 700,
                    textTransform: "uppercase",
                    letterSpacing: 0.6,
                    opacity: 0.55,
                  }}
                >
                  {label}
                </Typography>
                <Typography sx={{ fontSize: 12, fontWeight: 800 }}>
                  {value}
                </Typography>
              </Box>
            ))}
          </Box>
        )}

        {/* ── Table + Totals ── */}
        {items.length > 0 && (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
            {/* Table */}
            <Box sx={{ minWidth: 0 }}>
              <TableContainer sx={{ ...glass, overflowX: "auto" }}>
                <Table size="small">
                  <TableHead>
                    <TableRow
                      sx={{
                        background: isDark
                          ? "rgba(255,255,255,0.06)"
                          : "rgba(0,0,0,0.04)",
                      }}
                    >
                      {[
                        "Categoría",
                        "Concepto",
                        "Descripción",
                        "Desde",
                        "Hasta",
                        "Tipo",
                        "Tasa",
                        "Monto",
                        "IVA",
                        "",
                      ].map((h, i) => (
                        <TableCell
                          key={i}
                          align={i >= 6 && i !== 9 ? "right" : "left"}
                          sx={{
                            fontWeight: 700,
                            fontSize: 10,
                            textTransform: "uppercase",
                            letterSpacing: 0.5,
                            opacity: 0.5,
                            whiteSpace: "nowrap",
                            py: 1,
                          }}
                        >
                          {h}
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {items.map((item, idx) => {
                      const meta = item.metadata ?? {};
                      return (
                        <TableRow
                          key={idx}
                          sx={{
                            transition: "background 0.15s",
                            "&:hover": {
                              background: isDark
                                ? "rgba(255,255,255,0.04)"
                                : "rgba(0,0,0,0.025)",
                            },
                            "&:nth-of-type(even)": {
                              background: isDark
                                ? "rgba(255,255,255,0.02)"
                                : "rgba(0,0,0,0.015)",
                            },
                          }}
                        >
                          <TableCell sx={{ py: 1 }}>
                            <CategoryBadge
                              category={item.category}
                              isDark={isDark}
                            />
                          </TableCell>
                          <TableCell
                            sx={{
                              fontSize: 12,
                              fontWeight: 700,
                              whiteSpace: "nowrap",
                              py: 1,
                            }}
                          >
                            {item.name}
                          </TableCell>
                          <TableCell sx={{ fontSize: 11, opacity: 0.7, py: 1 }}>
                            {meta.jus_used && meta.multiplier
                              ? `JUS ${formatCurrency(meta.jus_used)} × ${meta.multiplier}`
                              : meta.fiscal_year
                                ? `Año ${meta.fiscal_year}`
                                : "—"}
                          </TableCell>
                          <TableCell
                            sx={{
                              fontSize: 11,
                              whiteSpace: "nowrap",
                              py: 1,
                            }}
                          >
                            {meta.desde ?? "—"}
                          </TableCell>
                          <TableCell
                            sx={{
                              fontSize: 11,
                              whiteSpace: "nowrap",
                              py: 1,
                            }}
                          >
                            {meta.hasta ?? "—"}
                          </TableCell>
                          <TableCell
                            sx={{ fontSize: 11, whiteSpace: "nowrap", py: 1 }}
                          >
                            {CALC_LABELS[item.calculation_type] ??
                              item.calculation_type}
                          </TableCell>
                          <TableCell align="right" sx={{ fontSize: 11, py: 1 }}>
                            {meta.tasa > 0 ? `${meta.tasa}%` : "—"}
                          </TableCell>
                          <TableCell align="right" sx={{ py: 1 }}>
                            <EditableAmountCell
                              value={item.amount}
                              onChange={(v) => handleItemAmountChange(idx, v)}
                            />
                            {item.category === "honorarios" && honorariosInJus && Number(calcResult?.jus_value) > 0 && (
                              <Typography sx={{ fontSize: 10, opacity: 0.55, mt: 0.2 }}>
                                {formatHonorarios(item.amount)}
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell align="right" sx={{ fontSize: 12, py: 1 }}>
                            {Number(item.iva_amount) > 0
                              ? (item.category === "honorarios" ? formatHonorarios(item.iva_amount) : formatCurrency(item.iva_amount))
                              : "—"}
                          </TableCell>
                          <TableCell
                            sx={{
                              py: 1,
                              pr: 1,
                              position: "sticky",
                              right: 0,
                              background:
                                idx % 2 === 1
                                  ? isDark
                                    ? "rgba(18,18,18,1)"
                                    : "rgba(249,249,249,1)"
                                  : isDark
                                    ? "rgba(30,30,30,1)"
                                    : "rgba(255,255,255,1)",
                            }}
                          >
                            <Tooltip title="Eliminar rubro">
                              <IconButton
                                size="small"
                                onClick={() => handleDeleteItem(idx)}
                                sx={{
                                  opacity: 0.35,
                                  "&:hover": {
                                    opacity: 1,
                                    color: "error.main",
                                  },
                                }}
                              >
                                <DeleteOutlineRoundedIcon
                                  sx={{ fontSize: 16 }}
                                />
                              </IconButton>
                            </Tooltip>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>

              {/* Add item row */}
              <Box sx={{ mt: 1, display: "flex", justifyContent: "flex-end" }}>
                <Tooltip title="Agregar rubro a la liquidación">
                  <Button
                    size="small"
                    startIcon={<AddRoundedIcon />}
                    onClick={(e) => {
                      setAddItemAnchor(e.currentTarget);
                      setAddItemSearch("");
                    }}
                    sx={{ borderRadius: 2, fontSize: 12 }}
                  >
                    Agregar rubro
                  </Button>
                </Tooltip>
              </Box>

              {/* Add item popover */}
              <Popover
                open={Boolean(addItemAnchor)}
                anchorEl={addItemAnchor}
                onClose={() => setAddItemAnchor(null)}
                anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                transformOrigin={{ vertical: "top", horizontal: "right" }}
                PaperProps={{
                  sx: { width: 340, borderRadius: 2.5, p: 1.5, mt: 0.5 },
                }}
              >
                <TextField
                  placeholder="Buscar rubro..."
                  value={addItemSearch}
                  onChange={(e) => setAddItemSearch(e.target.value)}
                  size="small"
                  fullWidth
                  autoFocus
                  sx={{ mb: 1 }}
                />
                <Box
                  sx={{
                    maxHeight: 280,
                    overflowY: "auto",
                    display: "flex",
                    flexDirection: "column",
                    gap: 0.25,
                  }}
                >
                  {(() => {
                    const usedIds = new Set(
                      items.map((it) => it.liquidation_item_type_id),
                    );
                    const filtered = availableItemTypes.filter(
                      (t) =>
                        !usedIds.has(t.id) &&
                        (!addItemSearch ||
                          t.name
                            .toLowerCase()
                            .includes(addItemSearch.toLowerCase()) ||
                          (CATEGORY_LABELS[t.category] ?? t.category)
                            .toLowerCase()
                            .includes(addItemSearch.toLowerCase())),
                    );
                    if (filtered.length === 0)
                      return (
                        <Typography
                          sx={{
                            fontSize: 12,
                            opacity: 0.5,
                            textAlign: "center",
                            py: 2,
                          }}
                        >
                          Sin resultados
                        </Typography>
                      );
                    return filtered.map((t) => (
                      <MenuItem
                        key={t.id}
                        onClick={() => handleAddItemType(t)}
                        sx={{
                          borderRadius: 1.5,
                          gap: 1,
                          alignItems: "center",
                          py: 0.8,
                        }}
                      >
                        <Box
                          sx={{
                            display: "inline-flex",
                            alignItems: "center",
                            px: 0.75,
                            py: 0.2,
                            borderRadius: 0.75,
                            background: "rgba(0,0,0,0.055)",
                            border: "1px solid rgba(0,0,0,0.1)",
                            flexShrink: 0,
                          }}
                        >
                          <Typography
                            sx={{
                              fontSize: 9,
                              fontWeight: 700,
                              textTransform: "uppercase",
                              letterSpacing: 0.5,
                              opacity: 0.65,
                              lineHeight: 1,
                            }}
                          >
                            {CATEGORY_LABELS[t.category] ?? t.category}
                          </Typography>
                        </Box>
                        <Typography sx={{ fontSize: 13, fontWeight: 600 }}>
                          {t.name}
                        </Typography>
                      </MenuItem>
                    ));
                  })()}
                </Box>
              </Popover>
            </Box>

            {/* Totals bar */}
            <Box
              sx={{
                ...glassPanel,
                display: "flex",
                alignItems: "center",
                flexWrap: "wrap",
                gap: 0,
                px: 2,
                py: 1.2,
              }}
            >
              {[
                { label: "Capital", value: totalCapital },
                { label: "Honorarios", value: totalHonorarios },
                { label: "IVA (21%)", value: totalIva },
                { label: "Gastos", value: totalGastos },
              ].map(({ label, value }, i, arr) => (
                <Box
                  key={label}
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "flex-start",
                    px: 2,
                    borderRight:
                      i < arr.length - 1
                        ? isDark
                          ? "1px solid rgba(255,255,255,0.08)"
                          : "1px solid rgba(0,0,0,0.08)"
                        : "none",
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: 9,
                      fontWeight: 700,
                      textTransform: "uppercase",
                      letterSpacing: 0.6,
                      opacity: 0.45,
                      mb: 0.2,
                    }}
                  >
                    {label}
                  </Typography>
                  <Typography sx={{ fontSize: 13, fontWeight: 800 }}>
                    {label === "Honorarios" || label === "IVA (21%)" ? formatHonorarios(value) : formatCurrency(value)}
                  </Typography>
                </Box>
              ))}
              <Box
                sx={{
                  ml: "auto",
                  pl: 2,
                  borderLeft: isDark
                    ? "1px solid rgba(255,255,255,0.1)"
                    : "1px solid rgba(0,0,0,0.1)",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "flex-end",
                }}
              >
                <Typography
                  sx={{
                    fontSize: 9,
                    fontWeight: 700,
                    textTransform: "uppercase",
                    letterSpacing: 0.6,
                    opacity: 0.45,
                    mb: 0.2,
                  }}
                >
                  Total
                </Typography>
                <Typography
                  sx={{
                    fontSize: 20,
                    fontWeight: 900,
                    color: "primary.main",
                    letterSpacing: -0.5,
                  }}
                >
                  {formatCurrency(grandTotal)}
                </Typography>
              </Box>
            </Box>

            {/* Notes */}
            <Box sx={{ mt: 1.5 }}>
              <TextField
                label="Notas (opcional)"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                multiline
                rows={2}
                size="small"
                fullWidth
              />
            </Box>
          </Box>
        )}

        {saveError && (
          <Alert severity="error" sx={{ mt: 1 }}>
            {saveError}
          </Alert>
        )}
      </Box>

      {/* ── Tributario search popover ── */}
      <Popover
        open={Boolean(searchAnchor)}
        anchorEl={searchAnchor}
        onClose={() => setSearchAnchor(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        PaperProps={{
          sx: { p: 2.5, width: 480, maxWidth: "95vw", borderRadius: 2 },
        }}
      >
        <Typography
          sx={{
            fontSize: 11,
            fontWeight: 700,
            textTransform: "uppercase",
            letterSpacing: 0.7,
            opacity: 0.5,
            mb: 1.5,
          }}
        >
          Buscar saldo en Tributario Municipal
        </Typography>

        <Box
          sx={{
            display: "flex",
            gap: 1,
            alignItems: "flex-end",
            flexWrap: "wrap",
          }}
        >
          <FormControl size="small" sx={{ minWidth: 190 }}>
            <InputLabel>Repartición</InputLabel>
            <Select
              value={searchRep}
              label="Repartición"
              onChange={(e) => setSearchRep(e.target.value)}
            >
              {REP_OPTIONS.map((opt) => (
                <MenuItem key={opt.cod} value={opt.cod}>
                  {opt.nombre.trim() || "—"}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <TextField
            label="Nro"
            size="small"
            type="number"
            value={searchNro}
            onChange={(e) => setSearchNro(e.target.value)}
            inputProps={{ min: 1 }}
            sx={{ width: 90 }}
          />

          <TextField
            label="Año"
            size="small"
            type="number"
            value={searchAnio}
            onChange={(e) => setSearchAnio(e.target.value)}
            inputProps={{ min: 2000, max: 2100 }}
            sx={{ width: 90 }}
          />

          <Button
            variant="contained"
            size="small"
            onClick={handleTributarioSearch}
            disabled={
              searchLoading || !searchRep.trim() || !searchNro || !searchAnio
            }
            startIcon={
              searchLoading ? (
                <CircularProgress size={14} color="inherit" />
              ) : (
                <SearchRoundedIcon />
              )
            }
            sx={{ borderRadius: 1.5, fontWeight: 700, height: 40 }}
          >
            Buscar
          </Button>
        </Box>

        {searchError && (
          <Alert
            severity="error"
            sx={{ mt: 1.5 }}
            onClose={() => setSearchError("")}
          >
            {searchError}
          </Alert>
        )}

        {searchFound && (
          <Box
            sx={{
              mt: 1.5,
              p: 1.5,
              borderRadius: 1.5,
              background: isDark
                ? "rgba(255,255,255,0.05)"
                : "rgba(0,0,0,0.03)",
              border: "1px solid",
              borderColor: "success.main",
              opacity: 0.9,
            }}
          >
            <Box
              sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.75 }}
            >
              <Chip
                label={`#${searchFound.jud_id}`}
                size="small"
                color="primary"
                sx={{ fontWeight: 800, fontSize: 11 }}
              />
              {searchFound.expe && (
                <Chip label={searchFound.expe} size="small" />
              )}
              {searchFound.est_nom && (
                <Chip
                  label={searchFound.est_nom}
                  size="small"
                  color={
                    searchFound.est_nom === "Pagado" ? "success" : "default"
                  }
                />
              )}
            </Box>
            {searchFound.nombre && (
              <Typography sx={{ fontSize: 12, fontWeight: 600, mb: 0.5 }}>
                {searchFound.nombre}
              </Typography>
            )}
            <Box sx={{ display: "flex", gap: 2, mt: 0.5 }}>
              <Box>
                <Typography sx={{ fontSize: 10, opacity: 0.55 }}>
                  Nominal
                </Typography>
                <Typography sx={{ fontSize: 12, fontWeight: 700 }}>
                  {formatCurrency(searchFound.nominal)}
                </Typography>
              </Box>
              <Box>
                <Typography sx={{ fontSize: 10, opacity: 0.55 }}>
                  Accesorios
                </Typography>
                <Typography sx={{ fontSize: 12, fontWeight: 700 }}>
                  {formatCurrency(searchFound.accesor)}
                </Typography>
              </Box>
              <Box>
                <Typography sx={{ fontSize: 10, opacity: 0.55 }}>
                  Saldo total
                </Typography>
                <Typography
                  sx={{ fontSize: 13, fontWeight: 900, color: "success.main" }}
                >
                  {formatCurrency(searchFound.balance)}
                </Typography>
              </Box>
            </Box>
            <Typography sx={{ fontSize: 10, mt: 1, opacity: 0.5 }}>
              ✓ Saldo aplicado al campo
            </Typography>
          </Box>
        )}
      </Popover>
    </AppDialog>
  );
}
